// sosTour.js — the Layered Read guided tour (Shepherd.js).
//
// A short orientation tour for someone arriving at the wheel for the first time: what the
// wheel is, what the colours mean, how to recolour it, where the detailed read lives, and
// where to go deeper. Auto-runs once on first visit (localStorage gate) and is always
// re-launchable from the "Take a tour" button. Built to run inside the React/Inertia page,
// mirroring the Therapy page's Shepherd pattern.
//
// Targets are stable ids added to the Sos2 components: #sos-wheel, #sos-legend,
// #metric-select, #sos-read-pane, #sos-read, #sos-view-toggle, #sos-help.

import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

const SEEN_KEY = 'sos_tour_seen';

export function shouldAutoStartSosTour() {
  try { return !window.localStorage.getItem(SEEN_KEY); } catch (e) { return false; }
}

function markSeen() {
  try { window.localStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* ignore */ }
}

// Build (and return) a fresh tour. Only steps whose target is on the page are added, so the
// tour never points at nothing (e.g. #sos-read is absent when a script has no synthesis).
export function buildSosTour({ onDone } = {}) {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    exitOnEsc: true,
    defaultStepOptions: {
      classes: 'sos-shepherd',
      scrollTo: { behavior: 'smooth', block: 'center' },
      cancelIcon: { enabled: true },
      arrow: true,
    },
  });

  const back = { text: 'Back', action: () => tour.back(), secondary: true };
  const next = { text: 'Next', action: () => tour.next() };
  const skip = { text: 'Skip', action: () => tour.cancel(), secondary: true };
  const done = { text: 'Done', action: () => tour.complete() };

  // Declarative step list; attach only those present. First/last buttons are fixed up after.
  const specs = [
    {
      sel: '#sos-wheel', on: 'right',
      title: 'Your whole script, one picture',
      text: 'This wheel is your entire screenplay. The centre is the whole script; the rings move outward through acts, sequences, and scenes — reading clockwise from the top.',
    },
    {
      sel: '#sos-legend', on: 'bottom',
      title: 'Colour = how much work',
      text: 'Colour shows how much work each part needs — green is landing, red is working harder than it lands. It’s a read, not a pass/fail grade: a map of where to look.',
    },
    {
      sel: '#sos-wheel', on: 'right',
      title: 'Every level is graded on its own',
      text: 'Because each ring is scored separately, the colour tells you <em>where</em> a problem lives — a whole act that needs reworking looks different from a run of individual scenes that do.',
    },
    {
      sel: '#metric-select', on: 'bottom',
      title: 'Recolour by any signal',
      text: 'Use <strong>Colour by</strong> to repaint the wheel by a single craft signal — Design, Execution, Read, or any of the twelve axes — to see where that one thing runs weak.',
    },
    {
      sel: '#sos-read-pane', on: 'left',
      title: 'Click a wedge for the detail',
      text: 'Click any wedge to open its detailed read here — what’s working, what’s dragging, and your options for fixing it.',
    },
    {
      sel: '#sos-read', on: 'bottom',
      title: 'Start with what matters most',
      text: 'Up top is the read — the most important findings first, including the single move that would clear the most drag across your script.',
    },
    {
      sel: '#sos-view-toggle', on: 'bottom',
      title: 'Other ways to look',
      text: 'Prefer a grid? Switch to <strong>Table</strong> for the same scores as a sortable list. The <strong>Patterns</strong> tab finds the habits you repeat across the script.',
    },
    {
      sel: '#sos-help', on: 'left',
      title: 'Never stuck on a term',
      text: 'Hit a word you don’t know? Every label has an <strong>(i)</strong>, and <strong>ⓘ How to read this</strong> opens the full guide any time. That’s the tour — dive in.',
    },
  ];

  const present = specs.filter((s) => document.querySelector(s.sel));
  present.forEach((s, i) => {
    const first = i === 0;
    const last = i === present.length - 1;
    tour.addStep({
      id: 'sos-' + i,
      title: s.title,
      text: s.text,
      attachTo: { element: s.sel, on: s.on },
      buttons: last ? [back, done] : (first ? [skip, next] : [back, next]),
    });
  });

  const finish = () => { markSeen(); if (onDone) onDone(); };
  tour.on('complete', finish);
  tour.on('cancel', finish);
  return tour;
}
